// Social Security calculation utilities with proper AIME estimation
import { gsPayTables } from '../data/gsPayTables';
import { localityAdjustments } from '../data/localityAdjustments';

// 2024 Social Security bend points (official SSA values)
export const SS_BEND_POINTS_2024 = {
  first: 1174,  // Monthly
  second: 7078  // Monthly
};

// Social Security wage bases by year
export const SS_WAGE_BASES = {
  2025: 176100,
  2024: 168600,
  2023: 160200,
  2022: 147000,
  2021: 142800,
  2020: 137700,
  2019: 132900,
  2018: 128400,
  2017: 127200,
  2016: 118500,
  2015: 118500,
  2014: 117000,
  2013: 113700,
  2012: 110100,
  2011: 106800,
  2010: 106800
};

// Get Social Security wage base for a given year
export const getSocialSecurityWageBase = (year) => {
  return SS_WAGE_BASES[year] || SS_WAGE_BASES[2010]; // Fallback to 2010
};

// Estimate starting grade based on current grade and service years
export const estimateStartingGrade = (bioData) => {
  const currentYear = new Date().getFullYear();
  const eodYear = new Date(bioData.entryOnDuty).getFullYear();
  const serviceYears = currentYear - eodYear;
  const currentGrade = bioData.currentGrade;
  
  if (serviceYears <= 0 || !currentGrade) {
    return { estimatedStartingGrade: 5, confidence: 'Low', progressionType: 'Unknown' };
  }
  
  // Calculate likely starting grade using career progression patterns
  const estimatedStartingGrade = calculateLikelyStartingGrade(currentGrade, serviceYears);
  const progressionType = determineProgressionType(estimatedStartingGrade, currentGrade, serviceYears);
  const confidence = calculateProgressionConfidence(serviceYears, progressionType);
  
  return {
    estimatedStartingGrade,
    progressionType,
    confidence,
    serviceYears,
    averageProgressionRate: (currentGrade - estimatedStartingGrade) / serviceYears
  };
};

const calculateLikelyStartingGrade = (currentGrade, serviceYears) => {
  // Realistic progression rates based on federal career patterns
  const maxProgressionRates = {
    earlyCareer: 0.4,   // 1 grade every 2.5 years (GS-5 to GS-11)
    midCareer: 0.25,    // 1 grade every 4 years (GS-9 to GS-12) 
    seniorCareer: 0.15  // 1 grade every 6.7 years (GS-13+)
  };
  
  // Determine appropriate progression rate
  let progressionRate;
  if (currentGrade <= 9) {
    progressionRate = maxProgressionRates.earlyCareer;
  } else if (currentGrade <= 12) {
    progressionRate = maxProgressionRates.midCareer;
  } else {
    progressionRate = maxProgressionRates.seniorCareer;
  }
  
  // Calculate maximum realistic advancement
  const maxAdvancement = Math.floor(serviceYears * progressionRate);
  
  // Work backwards from current grade
  const calculatedStart = Math.max(1, currentGrade - maxAdvancement);
  
  // Apply realistic constraints based on final achievement
  return applyStartingGradeConstraints(calculatedStart, currentGrade, serviceYears);
};

const applyStartingGradeConstraints = (calculated, currentGrade, serviceYears) => {
  // Minimum starting grades for various achievement levels
  const constraints = {
    15: { minStart: 7, typicalStart: 9, description: 'Senior Executive' },
    14: { minStart: 7, typicalStart: 9, description: 'Senior Manager' },
    13: { minStart: 5, typicalStart: 7, description: 'Team Lead/Specialist' },
    12: { minStart: 5, typicalStart: 7, description: 'Experienced Professional' },
    11: { minStart: 5, typicalStart: 5, description: 'Mid-Level Professional' },
    9:  { minStart: 5, typicalStart: 5, description: 'Entry Professional' },
    7:  { minStart: 5, typicalStart: 5, description: 'Recent Graduate' }
  };
  
  // Find appropriate constraint
  let constraint = constraints[currentGrade];
  if (!constraint) {
    // Use the closest lower grade constraint
    const availableGrades = Object.keys(constraints).map(Number).sort((a, b) => b - a);
    const closestGrade = availableGrades.find(grade => grade <= currentGrade);
    constraint = constraints[closestGrade] || { minStart: 5, typicalStart: 5 };
  }
  
  // Use typical start for longer careers, min start for shorter careers
  const floorGrade = serviceYears >= 15 ? constraint.typicalStart : constraint.minStart;
  
  return Math.max(calculated, floorGrade);
};

const determineProgressionType = (startGrade, currentGrade, serviceYears) => {
  const totalAdvancement = currentGrade - startGrade;
  const avgRate = totalAdvancement / serviceYears;
  
  if (avgRate >= 0.35) return 'Fast Track';
  if (avgRate >= 0.25) return 'Typical';
  if (avgRate >= 0.15) return 'Steady';
  return 'Conservative';
};

const calculateProgressionConfidence = (serviceYears, progressionType) => {
  if (serviceYears >= 20 && progressionType !== 'Fast Track') return 'High';
  if (serviceYears >= 10 && progressionType === 'Typical') return 'High';
  if (serviceYears >= 5) return 'Medium';
  return 'Low';
};

// Calculate AIME using improved federal career modeling
export const calculateImprovedAIME = (bioData) => {
  const birthYear = new Date(bioData.dateOfBirth).getFullYear();
  const eodYear = new Date(bioData.entryOnDuty).getFullYear();
  
  if (!birthYear || !eodYear || !bioData.currentGrade || !bioData.currentSalary) {
    return null; // Insufficient data
  }
  
  // Step 1: Estimate federal career progression
  const startGradeInfo = estimateStartingGrade(bioData);
  const federalProgression = calculateFederalSalaryProgression(bioData, startGradeInfo);
  
  // Step 2: Estimate pre-federal earnings
  const preFederalProgression = calculatePreFederalEarnings(birthYear, eodYear);
  
  // Step 3: Combine all earnings and get highest 35 years
  const allEarnings = [...preFederalProgression, ...federalProgression];
  const highest35Years = allEarnings
    .sort((a, b) => b.indexedEarnings - a.indexedEarnings)
    .slice(0, 35);
  
  // Step 4: Calculate AIME
  const totalIndexedEarnings = highest35Years.reduce((sum, year) => sum + year.indexedEarnings, 0);
  const aime = Math.round(totalIndexedEarnings / 12 / 35);
  
  return {
    aime,
    totalYears: allEarnings.length,
    federalYears: federalProgression.length,
    preFederalYears: preFederalProgression.length,
    startGradeEstimate: startGradeInfo.estimatedStartingGrade,
    progressionType: startGradeInfo.progressionType,
    confidence: startGradeInfo.confidence,
    highest35Years: highest35Years.map(y => ({ year: y.year, earnings: y.earnings, indexed: y.indexedEarnings }))
  };
};

// Calculate federal salary progression using realistic career model
const calculateFederalSalaryProgression = (bioData, startGradeInfo) => {
  const currentYear = new Date().getFullYear();
  const eodYear = new Date(bioData.entryOnDuty).getFullYear();
  const serviceYears = currentYear - eodYear;
  
  const progression = [];
  let grade = startGradeInfo.estimatedStartingGrade;
  let step = 1;
  let yearsAtCurrentGrade = 0;
  let yearsAtCurrentStep = 0;
  
  for (let year = 0; year < serviceYears; year++) {
    const calendarYear = eodYear + year;
    
    // Determine if promotion should occur
    if (shouldPromoteThisYear(grade, yearsAtCurrentGrade, bioData.currentGrade, serviceYears - year)) {
      grade++;
      step = Math.max(1, step - 1); // Small step penalty for promotion
      yearsAtCurrentGrade = 0;
      yearsAtCurrentStep = 0;
    }
    
    // Determine if step increase should occur
    if (step < 10 && shouldStepIncreaseThisYear(step, yearsAtCurrentStep)) {
      step++;
      yearsAtCurrentStep = 0;
    }
    
    // Calculate salary for this year
    const baseSalary = getHistoricalGSSalary(grade, step, calendarYear);
    const localizedSalary = applyLocalityAdjustment(baseSalary, bioData.locality, calendarYear);
    const cappedSalary = Math.min(localizedSalary, getSocialSecurityWageBase(calendarYear));
    
    // Apply wage indexing (simplified - would need actual AWI factors for precision)
    const indexingFactor = getSimpleIndexingFactor(calendarYear, currentYear);
    const indexedEarnings = cappedSalary * indexingFactor;
    
    progression.push({
      year: calendarYear,
      grade,
      step,
      earnings: cappedSalary,
      indexedEarnings
    });
    
    yearsAtCurrentGrade++;
    yearsAtCurrentStep++;
  }
  
  // Calibrate final salary to match current actual salary
  if (progression.length > 0) {
    const finalEntry = progression[progression.length - 1];
    const actualCurrentSalary = Math.min(bioData.currentSalary, getSocialSecurityWageBase(currentYear));
    const calibrationFactor = actualCurrentSalary / finalEntry.earnings;
    
    // Apply calibration to recent years
    const yearsToCalibrate = Math.min(3, progression.length);
    for (let i = progression.length - yearsToCalibrate; i < progression.length; i++) {
      progression[i].earnings *= calibrationFactor;
      progression[i].indexedEarnings *= calibrationFactor;
    }
  }
  
  return progression;
};

const shouldPromoteThisYear = (currentGrade, yearsAtGrade, finalGrade, yearsRemaining) => {
  if (currentGrade >= finalGrade) return false;
  
  const gradesNeeded = finalGrade - currentGrade;
  const timePerGrade = yearsRemaining / gradesNeeded;
  
  // Use deterministic promotion logic instead of random
  // This ensures consistent results across calculations
  if (currentGrade <= 9) return yearsAtGrade >= Math.max(2, Math.floor(timePerGrade));
  if (currentGrade <= 12) return yearsAtGrade >= Math.max(3, Math.floor(timePerGrade));
  return yearsAtGrade >= Math.max(4, Math.floor(timePerGrade));
};

const shouldStepIncreaseThisYear = (currentStep, yearsAtStep) => {
  if (currentStep <= 3) return yearsAtStep >= 1;
  if (currentStep <= 6) return yearsAtStep >= 2;
  return yearsAtStep >= 3;
};

const calculatePreFederalEarnings = (birthYear, eodYear) => {
  const workingStartYear = birthYear + 22; // Assume work starts at 22
  const preFederalYears = Math.max(0, eodYear - workingStartYear);
  
  if (preFederalYears <= 0) return [];
  
  const progression = [];
  const startingSalary = 32000; // Conservative starting salary
  const growthRate = 0.04; // 4% annual growth
  
  for (let i = 0; i < preFederalYears; i++) {
    const year = workingStartYear + i;
    const earnings = Math.min(startingSalary * Math.pow(1 + growthRate, i), getSocialSecurityWageBase(year));
    const indexingFactor = getSimpleIndexingFactor(year, new Date().getFullYear());
    
    progression.push({
      year,
      grade: 'Pre-Federal',
      step: null,
      earnings,
      indexedEarnings: earnings * indexingFactor
    });
  }
  
  return progression;
};

// Simplified historical salary lookup (would need actual historical data for precision)
const getHistoricalGSSalary = (grade, step, year) => {
  // Use current pay table as approximation, adjusting for inflation
  const currentSalary = gsPayTables.base[grade] ? gsPayTables.base[grade][step - 1] : 50000;
  
  // Rough inflation adjustment backwards (simplified)
  const inflationFactor = Math.pow(0.975, new Date().getFullYear() - year);
  return Math.round(currentSalary * inflationFactor);
};

const applyLocalityAdjustment = (baseSalary, locality, year) => {
  const rate = localityAdjustments[locality] || 1.15;
  return Math.round(baseSalary * rate);
};

// Simplified wage indexing (would need actual AWI factors for precision)
const getSimpleIndexingFactor = (earningsYear, indexYear) => {
  // Simplified approximation - real calculation needs AWI factors
  const yearsAgo = indexYear - earningsYear;
  return Math.pow(1.035, yearsAgo); // ~3.5% annual wage growth
};

// Calculate PIA from AIME using 2024 bend points
export const calculatePIAFromAIME = (aime) => {
  if (!aime || aime <= 0) return 0;
  
  const bendPoint1 = SS_BEND_POINTS_2024.first;
  const bendPoint2 = SS_BEND_POINTS_2024.second;
  
  let pia = 0;
  
  // First bend point: 90% of AIME up to $1,174
  if (aime <= bendPoint1) {
    pia = aime * 0.90;
  } else {
    pia = bendPoint1 * 0.90;
    
    // Second bend point: 32% of AIME between $1,174 and $7,078
    if (aime <= bendPoint2) {
      pia += (aime - bendPoint1) * 0.32;
    } else {
      pia += (bendPoint2 - bendPoint1) * 0.32;
      
      // Third tier: 15% of AIME above $7,078
      pia += (aime - bendPoint2) * 0.15;
    }
  }
  
  return Math.round(pia * 12); // Return annual PIA
};

// Get SSA link for detailed calculations
export const getSSACalculatorLink = () => {
  return {
    url: 'https://www.ssa.gov/benefits/retirement/planner/AnypiaApplet.html',
    title: 'Official SSA AIME Calculator',
    description: 'Use the Social Security Administration\'s official calculator for precise benefit estimates based on your actual earnings history.'
  };
};